/**
 * Job Flow Settings — admin-only.
 *
 * Two sections:
 *   • Email test mode toggle (default ON; switching OFF requires confirmation).
 *   • Pipelines editor — rename step label, flip admin-only, change default owner,
 *     add/remove steps, edit the step checklist.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    ArrowLeft, Loader2, Plus, Trash2, Save, AlertTriangle, ShieldAlert,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const JOB_TYPE_LABEL = {
    income_tax: "Income Tax Return", gst: "GST Return",
    payroll: "Payroll", others: "Others",
};

export default function JobFlowSettings() {
    const { user } = useAuth() || {};
    const isAdmin = user?.role === "firm_admin" || user?.role === "super_admin";

    const [settings, setSettings] = useState({ email_test_mode: true });
    const [pipelines, setPipelines] = useState([]);
    const [team, setTeam] = useState([]);
    const [loading, setLoading] = useState(true);
    const [confirmOpen, setConfirmOpen] = useState(false);

    async function load() {
        setLoading(true);
        try {
            const [{ data: s }, { data: p }, { data: t }] = await Promise.all([
                api.get("/job-flow/settings"),
                api.get("/job-flow/pipelines"),
                api.get("/job-flow/team"),
            ]);
            setSettings(s || { email_test_mode: true });
            setPipelines(p || []);
            setTeam(t || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }
    useEffect(() => { load(); }, []);

    async function flipTestMode(nextOn) {
        try {
            await api.put("/job-flow/settings", { email_test_mode: nextOn, confirm: true });
            setSettings({ ...settings, email_test_mode: nextOn });
            toast.success(nextOn ? "Test mode ON — nothing goes to real recipients." : "Test mode OFF — real emails will be sent.");
            setConfirmOpen(false);
        } catch (e) { toast.error(formatApiError(e)); }
    }

    async function savePipeline(idx, steps) {
        const p = pipelines[idx];
        try {
            await api.put(`/job-flow/pipelines/${p.type}`, { steps });
            toast.success(`${JOB_TYPE_LABEL[p.type]} pipeline saved`);
            await load();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    if (!isAdmin) {
        return (
            <div className="p-4"><p className="text-[#5E636E]">Admin only.</p></div>
        );
    }

    return (
        <div className="space-y-5" data-testid="jf-settings">
            <header className="flex items-center justify-between">
                <div>
                    <Link to="/app/job-flow" className="text-[12px] text-[#5E636E] hover:text-[#0F1115] inline-flex items-center gap-1">
                        <ArrowLeft className="h-3 w-3" /> Back to Job Flow
                    </Link>
                    <h1 className="text-xl font-semibold text-[#0F1115] mt-1">Job Flow — Settings</h1>
                </div>
            </header>

            {/* Pipelines */}
            {loading ? (
                <div className="py-10 text-center text-[#5E636E]"><Loader2 className="h-4 w-4 animate-spin inline" /> Loading…</div>
            ) : (
                <section className="space-y-4">
                    <h2 className="text-[14px] font-semibold text-[#0F1115]">Pipelines</h2>
                    {pipelines.map((p, idx) => (
                        <PipelineEditor key={p.type} pipeline={p} team={team} onSave={(steps) => savePipeline(idx, steps)} />
                    ))}
                </section>
            )}

            {confirmOpen && null}
        </div>
    );
}

function PipelineEditor({ pipeline, team, onSave }) {
    const [steps, setSteps] = useState(pipeline.steps || []);
    useEffect(() => { setSteps(pipeline.steps || []); }, [pipeline]);

    function patch(idx, p) {
        setSteps((xs) => xs.map((s, i) => i === idx ? { ...s, ...p } : s));
    }
    function remove(idx) {
        if (steps.length <= 1) { toast.error("Pipeline must have at least one step"); return; }
        setSteps((xs) => xs.filter((_, i) => i !== idx));
    }
    function add() {
        setSteps((xs) => [...xs, { key: `step_${xs.length + 1}`, label: "New step", admin_only: false, default_owner_user_id: null, checklist: [] }]);
    }
    function move(idx, dir) {
        const to = idx + dir;
        if (to < 0 || to >= steps.length) return;
        const copy = steps.slice();
        [copy[idx], copy[to]] = [copy[to], copy[idx]];
        setSteps(copy);
    }

    return (
        <div className="rounded-md border border-[#E4E4E7] bg-white" data-testid={`jf-pipeline-${pipeline.type}`}>
            <header className="px-4 py-2 border-b border-[#EEEDE7] flex items-center justify-between">
                <div className="font-medium text-[13px] text-[#0F1115]">{JOB_TYPE_LABEL[pipeline.type]} <span className="text-[11.5px] text-[#5E636E]">({steps.length} step{steps.length === 1 ? "" : "s"})</span></div>
                <Button size="sm" onClick={() => onSave(steps)} className="bg-[#0F1115] text-white hover:bg-[#2E2E2E]" data-testid={`jf-save-${pipeline.type}`}>
                    <Save className="h-3.5 w-3.5 mr-1" /> Save
                </Button>
            </header>
            <div className="p-3 space-y-2">
                {steps.map((s, i) => (
                    <div key={i} className="rounded-md border border-[#E4E4E7] p-2 bg-zinc-50/40">
                        <div className="grid grid-cols-12 gap-2 items-start">
                            <div className="col-span-1 text-[11px] text-[#5E636E] pt-2 text-center">
                                <div>{i + 1}</div>
                                <div className="flex flex-col gap-0.5 mt-1">
                                    <button onClick={() => move(i, -1)} className="hover:text-[#0F1115]" title="Move up">↑</button>
                                    <button onClick={() => move(i, +1)} className="hover:text-[#0F1115]" title="Move down">↓</button>
                                </div>
                            </div>
                            <div className="col-span-5">
                                <Label className="text-[11px]">Step label</Label>
                                <Input value={s.label} onChange={(e) => patch(i, { label: e.target.value })} className="h-7 text-[12.5px]" data-testid={`jf-step-label-${pipeline.type}-${i}`} />
                            </div>
                            <div className="col-span-4">
                                <Label className="text-[11px]">Default owner</Label>
                                <select value={s.default_owner_user_id || ""} onChange={(e) => patch(i, { default_owner_user_id: e.target.value || null })}
                                    className="w-full h-7 rounded-md border border-[#E4E4E7] bg-white px-2 text-[12.5px]" data-testid={`jf-step-owner-${pipeline.type}-${i}`}>
                                    <option value="">— None —</option>
                                    {team.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}{u.role === "firm_admin" ? " (Admin)" : ""}</option>)}
                                </select>
                            </div>
                            <div className="col-span-2 flex items-end gap-1 pt-5">
                                <label className="text-[11px] flex items-center gap-1">
                                    <input type="checkbox" checked={!!s.admin_only} onChange={(e) => patch(i, { admin_only: e.target.checked })} data-testid={`jf-step-admin-${pipeline.type}-${i}`} />
                                    Admin only
                                </label>
                                <button onClick={() => remove(i)} className="ml-auto text-[#5E636E] hover:text-red-700" title="Remove step" data-testid={`jf-step-remove-${pipeline.type}-${i}`}>
                                    <Trash2 className="h-3.5 w-3.5" />
                                </button>
                            </div>
                            <div className="col-span-11 col-start-2">
                                <Label className="text-[11px]">Checklist (one per line)</Label>
                                <Textarea rows={2} value={(s.checklist || []).join("\n")}
                                    onChange={(e) => patch(i, { checklist: e.target.value.split("\n").map((x) => x.trim()).filter(Boolean) })}
                                    className="text-[12px]" data-testid={`jf-step-checklist-${pipeline.type}-${i}`} />
                            </div>
                        </div>
                    </div>
                ))}
                <Button variant="outline" onClick={add} className="w-full" data-testid={`jf-step-add-${pipeline.type}`}>
                    <Plus className="h-3.5 w-3.5 mr-1" /> Add step
                </Button>
            </div>
        </div>
    );
}
